import { useCallback } from 'react';
import { gridRowSelectionIdsSelector, useGridApiContext } from '@mui/x-data-grid-pro';
import { DispatchLeg, isCancelledLoad } from '@api/Trinium/Endpoints/Dispatch/types';
import Tag from '@components/Tag';
import RenderCell from '@components/DataGrid/RenderCell';
import { CellProps } from '@components/DataGrid/types';
import LegStatusSelector from '@components/LegStatusSelector';
import { useAppSelector } from '@reduxStore';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { LegStatus } from '@api/Trinium/types';
import styles from '@components/DataGrid/RenderCell.module.css';
import { useFetchDispatchLegsByJobIdQuery } from '@api/Trinium/Endpoints/Dispatch/slice';

/**
 * Component for rendering the Leg column Grid Cells
 */
export default function Leg(props: CellProps<DispatchLeg>) {
	const { isBulk, isHazmat, isLive, isOverweight, isDisabled, legId } = props.row;
	const cancelled = isCancelledLoad(props.row);
	const hasNoLeg = !legId;
	const gridApiRef = useGridApiContext();
	/** Resolved on demand when the status popup opens — subscribing to the selection here would re-render every leg cell on any row change */
	const getSelectedRows = useCallback(() => Array.from(gridRowSelectionIdsSelector(gridApiRef).values()) as DispatchLeg[], [gridApiRef]);

	const { selectedTerminal } = useAppSelector((state) => state.applicationState);
	const { data: dispatchLegs } = useFetchDispatchLegsByJobIdQuery(
		{
			terminalNumber: String(selectedTerminal?.number),
			jobId: String(props.row.jobId)
		}, // Skip when not DL, this prevents any call on dispatch board, "View All..." actions will just hit cache
		{ skip: hasNoLeg || (getEnumValueByKey(LegStatus, props.row.legStatus) as LegStatus) !== LegStatus.DL }
	);

	const nextLegStatus = dispatchLegs?.find((leg) => leg.legNo === props.row.legNo + 1)?.legStatus;

	// Cancelled loads have no leg to show a status/flags for — surface that the load was cancelled.
	if (cancelled) {
		return (
			<RenderCell
				primarySlot={
					<Tag
						text='CANCELLED'
						tagStyle='cancelled'
						width={125}
						height={18}
						align='center'
					/>
				}
				disableIndentation
			/>
		);
	}

	// Not cancelled, but no leg to show a status for either (e.g. closed/purged, or legs deleted directly).
	if (hasNoLeg) {
		return <RenderCell disableIndentation />;
	}

	return (
		<RenderCell
			primarySlot={
				<LegStatusSelector
					leg={props.row}
					nextLegStatus={nextLegStatus}
					getSelectedRows={getSelectedRows}
				/>
			}
			secondarySlot={
				<div className={styles['leg-icon-toolbar']}>
					{/* A closed load keeps its leg + status, but flag that it is read-only. Cancelled loads never reach here. */}
					{isDisabled && (
						<Tag
							text='CLOSED'
							tagStyle='closed'
							dense
						/>
					)}
					<Tag
						text='LIVE'
						tagStyle={isLive ? 'live' : 'disabled'}
						dense
					/>
					<Tag
						text='HAZ'
						tagStyle={isHazmat ? 'haz' : 'disabled'}
						dense
					/>
					<Tag
						text='WGT'
						tagStyle={isOverweight ? 'wgt' : 'disabled'}
						dense
					/>
					<Tag
						text='BULK'
						tagStyle={isBulk ? 'bulk' : 'disabled'}
						dense
					/>
				</div>
			}
			disableIndentation
		/>
	);
}
